import { skipToken } from "@reduxjs/toolkit/query";
import { configurationTestResultIdSchema } from "@repo/app-types/entities/configurationTestResult";
import { copilotProposalIdSchema } from "@repo/app-types/entities/copilotProposal";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  asPlaybookId,
  type PlaybookCriterionNode,
  type PlaybookNodeId,
  type PlaybookPublishTarget,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { RecordingAssessmentFeedbackStatus } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import {
  Outlet,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { useRecordingAssessmentFeedbackForPlaybookQuery } from "../api/entities/playbook.js";
import { appPaths } from "../app/appPaths.js";
import { useCollapseNavWhileMounted } from "../app/navCollapseContext.js";
import {
  NotificationType,
  showNotification,
} from "../features/notificationsOverlay/notificationsSlice.js";
import {
  COPILOT_PROPOSAL_QUERY_PARAM,
  COPILOT_TEST_RESULT_QUERY_PARAM,
} from "../features/researchAgent/copilotDeepLinks.js";
import {
  clearEditorProposals,
  reportEditorProposals,
} from "../features/researchAgent/editorProposalsSlice.js";
import { useResearchAgentProps } from "../features/researchAgent/researchAgentContext.js";
import { PlaybookVersionHistoryButton } from "../features/versionHistory/playbookVersionHistoryButton.js";
import { useIsFeatureFlagEnabled } from "../helpers/featureFlags/hooks.js";
import { useAppDispatch } from "../hooks/hook.js";
import { useAutosaveDraft } from "../hooks/useAutosaveDraft.js";
import { useDraftSaveStatus } from "../hooks/useDraftSaveStatus.js";
import { usePlaybookEditor } from "../hooks/usePlaybookEditor.js";
import {
  findViewedEntry,
  isOutcomeSampleToolResultEntry,
  readTestResultSelection,
  useSampleToolResults,
  type TestResultSelection,
} from "../hooks/useSampleToolResults.js";
import { DraftSaveStatusIndicator } from "./draftSaveStatusIndicator.js";
import { DraftStatusPill } from "./draftStatusPill.js";
import type {
  EditorContainerRouteProps,
  PlaybookEditorOutletContext,
} from "./playbookEditorOutletContext.js";
import { PlaybookEditorRouteTabs } from "./playbookEditorRouteTabs.js";
import {
  buildPlaybookEditLocation,
  checkIsSamePlaybookEditorPathname,
  PlaybookSpecialSelectionId,
  type PlaybookEditorSelectionId,
} from "./playbookEditorUrlState.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export const PlaybookEditorPage: FC = () => {
  useCollapseNavWhileMounted();

  const navigate = useNavigate();
  const location = useLocation();
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId URL param");
  }

  // The shared route shell treats `new` as creation and every other param as a
  // branded playbook id; its child routes choose Edit, Feedback, or Settings.
  const isNewPlaybook = playbookIdParam === "new";
  const playbookId = isNewPlaybook ? null : asPlaybookId(playbookIdParam);

  // Publish and discard clear the state `navigationBlocker` reads and then
  // navigate in the same tick. `useBlocker` re-registers its predicate in an
  // effect, so the router would still run the pre-clear closure and prompt on
  // the way out — mark the editor's own exits as deliberate instead.
  const isClosingRef = useRef(false);

  // Memoized so handlePublish/handleDiscard's useCallback deps stay stable.
  const onClose = useCallback(() => {
    isClosingRef.current = true;
    navigate(appPaths.settingsPlaybooks.build());
  }, [navigate]);

  const isKnowledgeBaseEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.KnowledgeBase,
  );
  const {
    data,
    initialData,
    liveData,
    isSaving,
    hasUnsavedChanges,
    hasUnpublishedChanges,
    publishBlockedReason,
    lastSavedAt,
    onChange,
    onDiscardChanges,
    onPublishChanges,
    onPublishPartialChanges,
    onSaveDraft,
  } = usePlaybookEditor(playbookId);
  // Only open feedback: it drives the tab badge and the review navigation.
  // The review tab fetches the other statuses itself when it shows them.
  const {
    data: openFeedback = [],
    isLoading: isOpenFeedbackLoading,
    isError: isOpenFeedbackError,
  } = useRecordingAssessmentFeedbackForPlaybookQuery(
    playbookId ? { playbookId, status: "open" } : skipToken,
  );
  const openFeedbackCount = isOpenFeedbackLoading
    ? undefined
    : openFeedback.length;
  const dispatch = useAppDispatch();

  // Track which action is in progress (for button loading states)
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  // Every publish builds on the live revision, so one at a time.
  // NOTE: this only serializes publishes from this tab. Two devices publishing
  // (or saving drafts) at once are last-writer-wins with no conflict detection
  // — brittle, but accepted for now.
  const [publishingTarget, setPublishingTarget] =
    useState<PlaybookPublishTarget | null>(null);
  const isActionInProgress =
    isPublishing || isDiscarding || publishingTarget !== null;

  // Route-specific query state is authoritative while its route is active.
  // These values only bridge a tab transition after the source route drops its
  // query params (e.g. Feedback -> Settings -> Edit).
  const [tabSelectionId, setTabSelectionId] =
    useState<PlaybookEditorSelectionId | null>(null);
  const [reviewStatus, setReviewStatus] =
    useState<RecordingAssessmentFeedbackStatus>("open");

  // High-level playbook-editor variable that accumulates sample-tool
  // results as the assistant runs them. Populated by detecting agent
  // turn completion and pushing the captured `proposal_id`s
  // into `addIdsToFetch`. The criteria settings pane reads `results`
  // to render the agent's suggestion alongside the selected criterion.
  const {
    results: sampleToolResults,
    addIdsToFetch,
    addTestResultIdsToFetch,
    resolveResultForNode: resolveSampleToolResultForNode,
    clearResults: clearSampleToolResults,
  } = useSampleToolResults(playbookId);

  // Set up autosave for existing playbooks
  const { flushPendingSave } = useAutosaveDraft({
    hasChanges: hasUnsavedChanges,
    canAutosave: playbookId !== null && !isSaving,
    onSave: onSaveDraft,
  });

  // Accepting a proposal persists the draft edit before recording the decision:
  // the tree edit is already committed to local state (recordingAssessmentEditor
  // applyProposal commits it synchronously), so flushing here saves that exact
  // edit, and the resolve fires only once the save lands — a failed save never
  // leaves an applied proposal whose change was silently dropped. A discard
  // changes nothing on the node, so it resolves directly.
  const resolveProposalForNode = useCallback(
    (playbookNodeId: PlaybookNodeId, status: "applied" | "discarded"): void => {
      void (async () => {
        if (status === "applied" && !(await flushPendingSave())) {
          return;
        }
        resolveSampleToolResultForNode(playbookNodeId, status);
      })();
    },
    [flushPendingSave, resolveSampleToolResultForNode],
  );

  const researchAgent = useResearchAgentProps();

  // The chat marks the row the edit tab is showing — the proposal or test
  // result on the selected node, or the outcome's when the outcome pane is
  // open. The other tabs show none.
  const [viewedTreeItemKey, setViewedTreeItemKey] = useState<
    AssessmentTreeItemKey | undefined
  >();
  useEffect(() => {
    const viewedEntry = findViewedEntry(sampleToolResults, viewedTreeItemKey);
    dispatch(
      reportEditorProposals({
        viewedIds: viewedEntry ? [viewedEntry.id] : [],
        isSaving: false,
      }),
    );
  }, [dispatch, viewedTreeItemKey, sampleToolResults]);
  useEffect(
    () => () => {
      dispatch(clearEditorProposals());
    },
    [dispatch],
  );

  // Ask Alpharun's tools read the saved draft, so a pending edit is written
  // before a message goes out.
  const registerBeforeSend = researchAgent?.registerBeforeSend;
  useEffect(
    () => registerBeforeSend?.(flushPendingSave),
    [registerBeforeSend, flushPendingSave],
  );

  // Bumped each time the assistant requests a navigation to a playbook node
  // (criterion or section). The nonce makes re-clicking the same row
  // re-trigger downstream selection even when the id is unchanged.
  const [criterionSelectionRequest, setCriterionSelectionRequest] = useState<{
    id: PlaybookNodeId;
    selection: TestResultSelection;
    nonce: number;
  } | null>(null);
  const selectProposalNode = useCallback(
    (id: PlaybookNodeId, selection: TestResultSelection) => {
      setCriterionSelectionRequest((prev) => ({
        id,
        selection,
        nonce: (prev?.nonce ?? 0) + 1,
      }));

      const selectionId =
        selection === "outcome"
          ? PlaybookSpecialSelectionId.Outcome
          : selection === "ghost-criterion" || selection === "ghost-section"
            ? null
            : id;
      const editLocation = buildPlaybookEditLocation({
        playbookIdParam,
        selectionId,
      });
      if (location.pathname !== editLocation.pathname) {
        navigate(editLocation);
      }
    },
    [location.pathname, navigate, playbookIdParam],
  );

  // One-shot deep link from an Ask Alpharun proposal card (?proposalId=...)
  // or test result row (?testResultId=...): hydrate the row into the ghost
  // pipeline, then select its node once the fetched entry lands. A resolved
  // proposal fetches to nothing, so the selection simply never fires.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(COPILOT_PROPOSAL_QUERY_PARAM),
  ).data;
  const deepLinkTestResultId = configurationTestResultIdSchema.safeParse(
    searchParams.get(COPILOT_TEST_RESULT_QUERY_PARAM),
  ).data;
  const [pendingDeepLinkId, setPendingDeepLinkId] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!deepLinkProposalId && !deepLinkTestResultId) {
      return;
    }
    if (deepLinkProposalId) {
      addIdsToFetch([deepLinkProposalId]);
    }
    if (deepLinkTestResultId) {
      addTestResultIdsToFetch([deepLinkTestResultId]);
    }
    setPendingDeepLinkId(deepLinkProposalId ?? deepLinkTestResultId ?? null);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(COPILOT_PROPOSAL_QUERY_PARAM);
        next.delete(COPILOT_TEST_RESULT_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [
    deepLinkProposalId,
    deepLinkTestResultId,
    addIdsToFetch,
    addTestResultIdsToFetch,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!pendingDeepLinkId) {
      return;
    }
    const entry = sampleToolResults[pendingDeepLinkId];
    if (!entry) {
      return;
    }
    setPendingDeepLinkId(null);
    selectProposalNode(entry.playbook_node_id, readTestResultSelection(entry));
  }, [pendingDeepLinkId, sampleToolResults, selectProposalNode]);

  // Compute save status for display
  const saveStatus = useDraftSaveStatus({
    hasUnpublishedChanges,
    isSaving,
    lastSavedAt,
  });

  // Handle publish
  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    try {
      await onPublishChanges();
      // Publish deletes the playbook's sample-tool results server-side; drop
      // the matching client cache so ghosts/suggestions don't linger.
      clearSampleToolResults();
      onClose();
    } finally {
      setIsPublishing(false);
    }
  }, [onPublishChanges, clearSampleToolResults, onClose]);

  // Unlike a whole publish, the editor stays open: the rest of the draft is
  // still pending. The API accepts several targets; today's inline actions
  // intentionally send one at a time.
  const handlePublishPartialTarget = useCallback(
    async (target: PlaybookPublishTarget) => {
      setPublishingTarget(target);
      try {
        // Flush first so the stored draft contains what's being published —
        // otherwise a failed autosave could leave the draft claiming the
        // node's old state as pending, and a later whole publish would revert
        // what was just published.
        const isDraftSaved = await flushPendingSave();
        if (!isDraftSaved) {
          throw new Error("Draft could not be saved before publishing");
        }
        await onPublishPartialChanges([target]);
        if (target.type === "node") {
          // The server discarded this node's saved proposal too.
          resolveSampleToolResultForNode(target.node_id, "discarded");
        } else if (target.type === "outcome") {
          // Mirror the server's discard of outcome proposals locally, while
          // preserving unrelated criterion and section results.
          Object.values(sampleToolResults)
            .filter(isOutcomeSampleToolResultEntry)
            .forEach((result) => {
              resolveSampleToolResultForNode(
                result.playbook_node_id,
                "discarded",
              );
            });
        }
      } catch (error) {
        console.error("Failed to publish playbook changes:", error);
        showNotification(dispatch, {
          id: `playbook-publish-changes-error-${new Date().getTime().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "Couldn't publish changes",
          secondaryMessage: "Refresh the page and try again.",
        });
      } finally {
        setPublishingTarget(null);
      }
    },
    [
      flushPendingSave,
      onPublishPartialChanges,
      resolveSampleToolResultForNode,
      sampleToolResults,
      dispatch,
    ],
  );

  const handlePublishNode = useCallback(
    (nodeId: string) =>
      handlePublishPartialTarget({ type: "node", node_id: nodeId }),
    [handlePublishPartialTarget],
  );

  const handlePublishOutcome = useCallback(
    () => handlePublishPartialTarget({ type: "outcome" }),
    [handlePublishPartialTarget],
  );

  const handlePublishSummaryTemplate = useCallback(
    () => handlePublishPartialTarget({ type: "summary_template" }),
    [handlePublishPartialTarget],
  );

  // Handle discard - revert draft to live (and close only for new playbooks).
  // For new playbooks the editor closes (no live version to revert to); for
  // existing playbooks the user stays on the page showing the reverted data.
  const handleDiscard = useCallback(async () => {
    setIsDiscarding(true);
    try {
      await onDiscardChanges();
      // Discard deletes the playbook's sample-tool results server-side; drop
      // the matching client cache so ghosts/suggestions don't linger.
      clearSampleToolResults();
      if (isNewPlaybook) {
        onClose();
      }
    } finally {
      setIsDiscarding(false);
    }
  }, [onDiscardChanges, clearSampleToolResults, isNewPlaybook, onClose]);

  // Discard is destructive — it reverts the draft and server-side deletes
  // every saved AI sample-tool result for the playbook. Always confirm; copy
  // mentions the AI suggestions only when there are any to lose. Only
  // proposals count: a test result carries no `proposal_id`, and its row
  // survives navigation, so leaving loses nothing.
  const hasSampleToolSuggestions = Object.values(sampleToolResults).some(
    (entry) => entry.proposal_id !== null,
  );

  // A new playbook has nothing to fall back on — no draft is created until
  // publish, so leaving discards the work.
  const hasUnpublishedNewPlaybook = isNewPlaybook && hasUnsavedChanges;

  // Block exits while this page holds work that can't be recovered: an
  // unpublished new playbook, or AI suggestions, which live only in memory and
  // can't be rebuilt without re-running the agent. Child-route changes for the
  // same playbook keep this shell mounted, so Edit/Feedback/Settings are safe.
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isClosingRef.current &&
      (hasSampleToolSuggestions || hasUnpublishedNewPlaybook) &&
      currentLocation.pathname !== nextLocation.pathname &&
      !checkIsSamePlaybookEditorPathname(
        nextLocation.pathname,
        playbookIdParam,
      ),
  );
  const {
    renderConfirmationModal: renderDiscardConfirmationModal,
    onConfirmThenClose: confirmThenDiscard,
  } = useConfirmationModal({
    needsConfirmation: true,
    onClose: () => {
      void handleDiscard();
    },
  });

  // Handler for applying AI assistant operations
  // The node-level Test / Optimize affordances hand off to Ask Alpharun: open
  // its panel and start a thread with the prompt. The playbook is grounded by
  // the editor route's page context, sent with the message.
  const askAlpharun = useCallback(
    (message: string) => {
      researchAgent?.onOpenChat();
      researchAgent?.onNewThreadSelect({ initialUserMessageText: message });
    },
    [researchAgent],
  );

  const handleOptimizeCriteria = useCallback(
    (criteria: PlaybookCriterionNode) => {
      askAlpharun(buildOptimizeCriteriaUserMessage(criteria));
    },
    [askAlpharun],
  );

  const handleTestCriteria = useCallback(
    (criteria: PlaybookCriterionNode) => {
      askAlpharun(buildTestCriteriaUserMessage(criteria));
    },
    [askAlpharun],
  );

  const handleOptimizeSection = useCallback(
    (section: PlaybookSectionNode) => {
      askAlpharun(buildOptimizeSectionUserMessage(section));
    },
    [askAlpharun],
  );

  const handleTestSection = useCallback(
    (section: PlaybookSectionNode) => {
      askAlpharun(buildTestSectionUserMessage(section));
    },
    [askAlpharun],
  );

  const handleOptimizeOutcome = useCallback(
    (outcome: OutcomeDefinition) => {
      askAlpharun(buildOptimizeOutcomeUserMessage(outcome));
    },
    [askAlpharun],
  );

  const handleTestOutcome = useCallback(
    (outcome: OutcomeDefinition) => {
      askAlpharun(buildTestOutcomeUserMessage(outcome));
    },
    [askAlpharun],
  );

  const editorProps = {
    data,
    initialOutcome: initialData?.outcome ?? null,
    liveOutcome: liveData?.outcome,
    liveSummaryTemplateBody: liveData?.summaryTemplateBody,
    liveTree: liveData?.tree,
    onChange,
    onOptimizeCriterion: playbookId ? handleOptimizeCriteria : undefined,
    onTestCriterion: playbookId ? handleTestCriteria : undefined,
    onOptimizeSection: playbookId ? handleOptimizeSection : undefined,
    onTestSection: playbookId ? handleTestSection : undefined,
    onOptimizeOutcome: playbookId ? handleOptimizeOutcome : undefined,
    onTestOutcome: playbookId ? handleTestOutcome : undefined,
    onPublishNode: playbookId ? handlePublishNode : undefined,
    onPublishOutcome: playbookId ? handlePublishOutcome : undefined,
    onPublishSummaryTemplate: playbookId
      ? handlePublishSummaryTemplate
      : undefined,
    publishingNodeId:
      publishingTarget?.type === "node" ? publishingTarget.node_id : undefined,
    isPublishingOutcome: publishingTarget?.type === "outcome",
    isPublishingSummaryTemplate: publishingTarget?.type === "summary_template",
    isPublishBlocked: isActionInProgress,
    sampleToolResults,
    onResolveSampleToolResultForNode: resolveProposalForNode,
    playbookId,
    criterionSelectionRequest,
  } satisfies EditorContainerRouteProps;

  const outletContext = {
    playbookIdParam,
    playbookId,
    data,
    liveTree: liveData?.tree,
    openFeedback,
    isOpenFeedbackLoading,
    isOpenFeedbackError,
    showsKnowledgeBase: isKnowledgeBaseEnabled ?? false,
    onChange,
    editorProps,
    onTabSelectionChange: setTabSelectionId,
    onViewedTreeItemChange: setViewedTreeItemKey,
    reviewStatus,
    onReviewStatusChange: setReviewStatus,
  } satisfies PlaybookEditorOutletContext;

  return (
    <>
      {renderDiscardConfirmationModal({
        title: "Discard changes?",
        message: hasSampleToolSuggestions
          ? "You'll lose any unpublished draft changes and the saved AI suggestions for this playbook."
          : "You'll lose any unpublished draft changes for this playbook.",
        confirmText: "Discard",
      })}
      <ConfirmationModal
        isOpen={navigationBlocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && navigationBlocker.state === "blocked") {
            navigationBlocker.reset();
          }
        }}
        title="Leave the playbook editor?"
        confirmText="Leave"
        onConfirm={() => {
          clearSampleToolResults();
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
          }
        }}
      >
        {hasUnpublishedNewPlaybook
          ? hasSampleToolSuggestions
            ? "This playbook hasn't been published yet, and you have unsaved AI suggestions for it. Both will be lost if you navigate away."
            : "This playbook hasn't been published yet. It'll be lost if you navigate away."
          : "You have unsaved AI suggestions for this playbook. They'll be lost if you navigate away."}
      </ConfirmationModal>
      <div className="flex h-full flex-col">
        <PageHeader
          title={data?.name}
          isLoading={!data}
          tabs={
            <PlaybookEditorRouteTabs
              playbookIdParam={playbookIdParam}
              playbookId={playbookId}
              openFeedbackCount={openFeedbackCount}
              feedbackTree={data?.tree}
              lastStableSelectionId={tabSelectionId}
              reviewStatus={reviewStatus}
            />
          }
          actions={
            <>
              {/* Status indicators - only show for existing playbooks. "Saving
                changes" text sits to the left of the Draft pill. */}
              {playbookId && (
                <>
                  <DraftSaveStatusIndicator status={saveStatus} />
                  <DraftStatusPill isLive={!hasUnpublishedChanges} />
                </>
              )}
              {hasUnpublishedChanges && (
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={confirmThenDiscard}
                  isDisabled={isActionInProgress}
                  isLoading={isDiscarding}
                  size={Size.MEDIUM}
                >
                  Discard
                </Button>
              )}
              <TooltipTrigger isDisabled={publishBlockedReason === null}>
                <TooltipHoverTarget
                  shape="rounded"
                  isDisabled={publishBlockedReason === null}
                >
                  <Button
                    variant={ButtonVariant.Primary}
                    onClick={handlePublish}
                    isDisabled={
                      !hasUnpublishedChanges ||
                      !data ||
                      isActionInProgress ||
                      publishBlockedReason !== null
                    }
                    isLoading={isPublishing}
                    size={Size.MEDIUM}
                  >
                    Publish
                  </Button>
                </TooltipHoverTarget>
                <SimpleTooltip>{publishBlockedReason}</SimpleTooltip>
              </TooltipTrigger>
              {playbookId ? (
                <PlaybookVersionHistoryButton
                  playbookId={playbookId}
                  isDisabled={isActionInProgress || hasUnsavedChanges}
                  onRollbackComplete={clearSampleToolResults}
                />
              ) : null}
            </>
          }
        />

        <Outlet context={outletContext} />
      </div>
    </>
  );
};

function buildOptimizeCriteriaUserMessage(criteria: PlaybookCriterionNode) {
  return `Help me optimize the "${criteria.name}" criterion. Look it up in the current playbook configuration first.

I'm not sure yet exactly what's wrong — walk me through the options, then once we've agreed on a direction, propose an updated criterion I can review in the editor (validate it against call history). Things I might want:
- Make grading more consistent
- Improve outcome correlation
- Reduce score clustering
- Clarify when this criterion should be marked N/A
- Or run a broad diagnostic and recommend the best improvement`;
}

function buildTestCriteriaUserMessage(criteria: PlaybookCriterionNode) {
  return `Test the "${criteria.name}" criterion as it stands today — do not propose a rewrite.

Look up this criterion's ID in the current Playbook configuration, then call test-playbook-node for it with no candidate draft, so it measures the criterion exactly as saved.
Common things to surface from the test (not exhaustive):
- The score distribution across recent gradings — which buckets dominate and which are rare.
- The outcome correlation against the team's outcome definition.
- Patterns worth flagging, e.g. the criterion rarely fires, the distribution is skewed, or correlation with the outcome is weak.

Share the findings. Only draft a replacement criterion if I explicitly ask for one in a follow-up.`;
}

function buildOptimizeSectionUserMessage(section: PlaybookSectionNode) {
  return `Help me optimize the "${section.name}" section. Look it up in the current playbook configuration first.

I'm not sure yet exactly what's wrong — walk me through the options, then once we've agreed on a direction, propose an updated section I can review in the editor (validate it against call history). Things I might want:
- Clarify when this section applies
- Reduce over-application or under-application
- Improve the criteria inside this section
- Restructure or split this section
- Or run a broad diagnostic and recommend the best improvement`;
}

function buildTestSectionUserMessage(section: PlaybookSectionNode) {
  return `Test the "${section.name}" section as it stands today — do not propose a rewrite.

Look up this section's ID in the current Playbook configuration, then call test-playbook-node for it with no candidate draft, so it measures the section exactly as saved.
Common things to surface from the test (not exhaustive):
- The applicable vs not-applicable distribution across recent gradings.
- Whether the applicability rubric is clear, consistent, observable from the transcript, and non-overlapping with other sections.
- Patterns worth flagging, e.g. the section is almost always applicable (or almost never), or its boundaries are inconsistent.

Share the findings. Only draft a replacement section if I explicitly ask for one in a follow-up.`;
}

function buildOptimizeOutcomeUserMessage(outcome: OutcomeDefinition) {
  return `Help me optimize the playbook's "${outcome.name}" outcome rubric.

I'm not sure yet exactly what's wrong — walk me through the options, then once we've agreed on a direction, propose an updated rubric I can review in the editor (validate it against call history). Things I might want:
- Improve classification consistency
- Reduce ambiguous edge cases between outcome labels
- Check whether the outcome definition matches real calls
- Or run a broad diagnostic and recommend the best improvement`;
}

function buildTestOutcomeUserMessage(outcome: OutcomeDefinition) {
  return `Test the playbook's "${outcome.name}" outcome rubric as it stands today — do not propose a rewrite.

Call test-playbook-outcome with no candidate draft, so it measures the rubric exactly as saved.
Common things to surface from the test (not exhaustive):
- The distribution of calls across the outcome's result values (e.g. won / lost / N/A).
- Whether the rubric is clear, consistent, and observable from the transcript alone.
- Patterns worth flagging, e.g. one result value dominates, or N/A is assigned far more or less than expected.

Share the findings. Only draft a replacement rubric if I explicitly ask for one in a follow-up.`;
}
